import {
  Component,
  computed,
  Input,
  OnInit,
  signal,
  Signal,
  WritableSignal,
} from '@angular/core';
import { ModalController } from '@ionic/angular';
import { AttendeeInformation } from 'src/app/interfaces/interfaces';
import { AttendeesStore } from 'src/app/stores/attendees.store';
import {
  attendeeIdentityKeys,
  attendeesShareIdentity,
  deduplicateAttendeesByUpn,
  isResourceAttendee,
  normalizeAttendeeUpn,
} from 'src/app/utils/checkout-delegates';

interface AttendeeAvailabilityState {
  isLoading: boolean;
  isAvailable?: boolean;
}

@Component({
  selector: 'app-add-attendee',
  templateUrl: './add-attendee.component.html',
  styleUrls: ['./add-attendee.component.scss'],
})
export class AddAttendeeComponent implements OnInit {
  @Input() initialAttendees: AttendeeInformation[] = [];
  @Input() startTime: string;
  @Input() endTime: string;
  @Input() bookingType: string;
  @Input() purpose: 'forward' | null = null;
  @Input() showDelegateAction = false;

  addedAttendees: WritableSignal<AttendeeInformation[]> = signal([]);
  searchTerm: WritableSignal<string> = signal('');

  private readonly availabilityState: WritableSignal<
    Record<string, AttendeeAvailabilityState>
  > = signal({});

  attendeesList: Signal<AttendeeInformation[]> = computed(() => {
    const candidates = this.getCandidates();
    const selected = this.addedAttendees();
    const state = this.availabilityState();

    return candidates
      .filter((attendee) => !this.containsAttendee(selected, attendee))
      .filter((attendee) => this.matchesSearch(attendee, this.searchTerm()))
      .map((attendee) => {
        const attendeeState = this.getAvailabilityState(attendee, state);

        return this.cloneAttendee(attendee, {
          isLoading: attendeeState?.isLoading ?? attendee.isLoading ?? false,
          isAvailable:
            attendeeState && 'isAvailable' in attendeeState
              ? attendeeState.isAvailable
              : attendee.isAvailable,
        });
      });
  });

  constructor(
    private modalController: ModalController,
    public attendeesStore: AttendeesStore,
  ) {}

  ngOnInit() {
    const clonedInitialAttendees = this.deduplicateAttendees(
      this.initialAttendees,
    ).map((attendee) => this.cloneAttendee(attendee, { isLoading: false }));

    this.addedAttendees.set(clonedInitialAttendees);
    this.seedAvailabilityState(clonedInitialAttendees);

    if (this.bookingType === 'edit' && clonedInitialAttendees.length > 0) {
      void this.refreshInitialAvailability(clonedInitialAttendees);
    }
  }

  async selectAttendee(attendee: AttendeeInformation) {
    if (isResourceAttendee(attendee)) return;

    await this.addAttendee(attendee);
  }

  toggleOptional(attendee: AttendeeInformation) {
    this.addedAttendees.update((attendees) =>
      attendees.map((current) =>
        attendeesShareIdentity(current, attendee)
          ? this.cloneAttendee(current, { isOptional: current.isOptional !== true })
          : current,
      ),
    );
  }

  toggleCheckoutDelegate(attendee: AttendeeInformation) {
    this.addedAttendees.update((attendees) =>
      attendees.map((current) =>
        attendeesShareIdentity(current, attendee)
          ? this.cloneAttendee(current, { canCheckout: current.canCheckout !== true, })
          : current,
      ),
    );
  }

  removeAttendee(attendee: AttendeeInformation) {
    this.addedAttendees.update((attendees) =>
      attendees.filter((current) => !attendeesShareIdentity(current, attendee)),
    );
  }

  filterAttendees(event: CustomEvent<{ value?: string | null }>) {
    this.searchTerm.set(event.detail?.value ?? '');
  }

  isRemovableAttendee(attendee: AttendeeInformation): boolean {
    return !(
      this.purpose === 'forward' &&
      this.initialAttendees.some((initialAttendee) =>
        attendeesShareIdentity(initialAttendee, attendee),
      )
    );
  }

  isSelected(attendee: AttendeeInformation): boolean {
    return this.containsAttendee(this.addedAttendees(), attendee);
  }

  dismissModal() {
    this.modalController.dismiss();
  }

  onDone() {
    const result = this.addedAttendees().map((attendee) =>
      this.cloneAttendee(attendee, { isLoading: false }),
    );
    this.modalController.dismiss(result);
  }

  private async addAttendee(attendee: AttendeeInformation) {
    const currentState = this.getAvailabilityState(attendee);

    if (currentState?.isLoading || this.isSelected(attendee)) return;

    this.setAvailabilityState(attendee, {
      isLoading: true,
      isAvailable: undefined,
    });

    try {
      const response = await this.attendeesStore.checkAttendeeAvailability(
        [attendee.userPrincipalName],
        this.startTime,
        this.endTime,
      );
      const isAvailable = this.getAvailabilityResult(response, attendee);
      this.setAvailabilityState(attendee, {
        isLoading: false,
        isAvailable,
      });
      this.addedAttendees.update((attendees) => [
        ...attendees,
        this.cloneAttendee(attendee, {
          isLoading: false,
          isAvailable,
        }),
      ]);
    } catch (error) {
      this.setAvailabilityState(attendee, {
        isLoading: false,
        isAvailable: undefined,
      });
      console.error(error);
    }
  }

  private async refreshInitialAvailability(
    attendees: AttendeeInformation[],
  ): Promise<void> {
    const initialAttendees = this.deduplicateAttendees(attendees);
    const upns = initialAttendees
      .map((attendee) => attendee.userPrincipalName)
      .filter(Boolean);

    initialAttendees.forEach((attendee) =>
      this.setAvailabilityState(attendee, {
        isLoading: true,
        isAvailable: undefined,
      }),
    );

    try {
      const response = await this.attendeesStore.checkAttendeeAvailability(
        upns,
        this.startTime,
        this.endTime,
      );
      initialAttendees.forEach((attendee) => {
        this.setAvailabilityState(attendee, {
          isLoading: false,
          isAvailable: this.getAvailabilityResult(response, attendee),
        });
      });
      this.addedAttendees.update((currentAttendees) =>
        currentAttendees.map((attendee) => {
          const initialAttendee = initialAttendees.find((candidate) =>
            attendeesShareIdentity(candidate, attendee),
          );
          if (!initialAttendee) return attendee;

          return this.cloneAttendee(attendee, {
            isLoading: false,
            isAvailable: this.getAvailabilityResult(response, initialAttendee),
          });
        }),
      );
    } catch (error) {
      initialAttendees.forEach((attendee) =>
        this.setAvailabilityState(attendee, {
          isLoading: false,
          isAvailable: undefined,
        }),
      );
      this.addedAttendees.update((currentAttendees) =>
        currentAttendees.map((attendee) => {
          if (!this.containsAttendee(initialAttendees, attendee)) {
            return attendee;
          }

          return this.cloneAttendee(attendee, {
            isLoading: false,
            isAvailable: undefined,
          });
        }),
      );
      console.error(error);
    }
  }

  private getCandidates(): AttendeeInformation[] {
    return this.deduplicateAttendees(this.attendeesStore.attendees());
  }

  private seedAvailabilityState(attendees: AttendeeInformation[]) {
    const state = { ...this.availabilityState() };
    attendees.forEach((attendee) => {
      const attendeeState = {
        isLoading: false,
        isAvailable: attendee.isAvailable,
      };
      attendeeIdentityKeys(attendee).forEach((identity) => {
        state[identity] = attendeeState;
      });
    });
    this.availabilityState.set(state);
  }

  private getAvailabilityState(
    attendee: AttendeeInformation,
    state = this.availabilityState(),
  ): AttendeeAvailabilityState | undefined {
    return attendeeIdentityKeys(attendee)
      .map((identity) => state[identity])
      .find((attendeeState) => attendeeState !== undefined);
  }

  private setAvailabilityState(
    attendee: AttendeeInformation,
    state: AttendeeAvailabilityState,
  ) {
    const identities = attendeeIdentityKeys(attendee);
    if (identities.length === 0) return;

    this.availabilityState.update((current) => ({
      ...current,
      ...Object.fromEntries(
        identities.map((identity) => [identity, state] as const),
      ),
    }));
  }

  private getAvailabilityResult(
    response: Record<string, boolean>,
    attendee: AttendeeInformation,
  ): boolean | undefined {
    const requestedKeys = new Set(attendeeIdentityKeys(attendee));
    const matchingKey = Object.keys(response || {}).find((key) =>
      requestedKeys.has(normalizeAttendeeUpn(key)),
    );
    return matchingKey === undefined ? undefined : response[matchingKey];
  }

  private matchesSearch(
    attendee: AttendeeInformation,
    searchTerm: string,
  ): boolean {
    const normalizedSearchTerm = normalizeAttendeeUpn(searchTerm);
    if (!normalizedSearchTerm) return true;

    return [
      attendee.firstName,
      attendee.lastName,
      `${attendee.firstName || ''} ${attendee.lastName || ''}`,
      attendee.userPrincipalName,
      ...(attendee.userAliases ?? []),
    ].some((value) =>
      normalizeAttendeeUpn(value).includes(normalizedSearchTerm),
    );
  }

  private containsAttendee(
    attendees: AttendeeInformation[],
    attendee: AttendeeInformation,
  ): boolean {
    return attendees.some((current) =>
      attendeesShareIdentity(current, attendee),
    );
  }

  private deduplicateAttendees(
    attendees: AttendeeInformation[],
  ): AttendeeInformation[] {
    return deduplicateAttendeesByUpn(attendees);
  }

  private cloneAttendee(
    attendee: AttendeeInformation,
    overrides: Partial<AttendeeInformation> = {},
  ): AttendeeInformation {
    return {
      ...attendee,
      userAliases: attendee.userAliases ? [...attendee.userAliases] : [],
      proposedNewTime: attendee.proposedNewTime
        ? { ...attendee.proposedNewTime }
        : null,
      ...overrides,
    };
  }
}
